import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import ReferenceModal from "../../approval/ReferenceModal";

export default function IncidentEditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const attachmentRef = useRef(null);
  const editorRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const normalizeTitle = (t) => {
    const v = String(t || "").trim();
    return v && v !== "(제목 없음)" ? v : "";
  };

  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const meId = me?.id || null;

  const [form, setForm] = useState({
    doc_date: initial.doc_date || new Date().toISOString().slice(0, 10),
    title: normalizeTitle(initial.title),
    note: initial.note || "",
    status: initial.status || "none",
  });

  const [meta, setMeta] = useState({
    incident_no: initial.incident_no || "",
    incident_date:
      initial.incident_date || new Date().toISOString().slice(0, 10),
    location: initial.location || "",
    incident_type: initial.incident_type || "",
    severity: initial.severity || "MODERATE",
    severity_note: initial.severity_note || "",
    reporter: initial.reporter || "",
    witnesses: Array.isArray(initial.witnesses) ? initial.witnesses : [],
    minor_injury_count: Number(initial.minor_injury_count ?? 0),
    major_injury_count: Number(initial.major_injury_count ?? 0),
    fatality_count: Number(initial.fatality_count ?? 0),
    property_damage_cost: Number(initial.property_damage_cost ?? 0),
    property_damage_note: initial.property_damage_note || "",
    description: initial.description || "",
    cause: initial.cause || "",
    corrective_action: initial.corrective_action || "",
    preventive_action: initial.preventive_action || "",
  });

  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [referenceUserDetails, setReferenceUserDetails] = useState([]);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

  const openReferenceModal = () => setIsReferenceModalOpen(true);
  const closeReferenceModal = () => setIsReferenceModalOpen(false);

  const handleReferenceChange = (ids) => setReferenceUserIds(ids);

  const handleReferenceSave = (ids, usersForFront = []) => {
    setReferenceUserIds(ids);
    setReferenceUserDetails(
      (usersForFront || []).filter((u) => u && u.id !== meId)
    );
    return true;
  };

  const [photos] = useState(initial.photos || []);

  const onForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onMeta = (k, v) => setMeta((p) => ({ ...p, [k]: v }));

  const addWitness = () =>
    onMeta("witnesses", [
      ...(meta.witnesses || []),
      { id: crypto.randomUUID(), name: "" },
    ]);

  const updateWitness = (id, name) =>
    onMeta(
      "witnesses",
      (meta.witnesses || []).map((w, i) => {
        const key = w?.id ?? i;
        return key === id ? (typeof w === "string" ? name : { ...w, name }) : w;
      })
    );

  const removeWitness = (id) =>
    onMeta(
      "witnesses",
      (meta.witnesses || []).filter((w, i) => (w?.id ?? i) !== id)
    );

  const autoTitle = useMemo(() => {
    const t = meta.incident_type?.trim();
    const d = (meta.incident_date || form.doc_date || today)
      .toString()
      .slice(0, 10);
    const l = meta.location?.trim();
    if (t && l) return `[사고] ${t} - ${l} (${d})`;
    if (t) return `[사고] ${t} (${d})`;
    if (l) return `[사고] ${l} (${d})`;
    return `[사고] ${d}`;
  }, [meta.incident_type, meta.location, meta.incident_date]);

  useEffect(() => {
    if (!Array.isArray(initial.reference_users)) return;

    const myId = Number(meId);

    const filtered = initial.reference_users.filter(
      (id) => Number(id) !== myId
    );

    setReferenceUserIds(filtered);

    setReferenceUserDetails(
      (initial.reference_user_details || []).filter((u) => u.id !== myId)
    );
  }, [initial, meId]);

  useEffect(() => {
    if (!onChange) return;
    const finalTitle = form.title?.trim() || autoTitle?.trim();

    const refUsers = [...referenceUserIds];
    if (meId) {
      refUsers.push(meId);
    }

    onChange({
      dns: initial.dns || "",
      doc_type: "incident",
      title: finalTitle,
      status: form.status,
      attachmentRef,
      meta: {
        ...meta,
        doc_date: form.doc_date || today,
        note: form.note,
        photos,
        reference_users: refUsers,
      },
    });
  }, [form, meta, photos, autoTitle, referenceUserIds, meId]);

  const editing = !readOnly;

  return (
    <div className="space-y-4 px-2 py-4">
      {/* 문서 정보 (모바일 최적화) */}
      <section className="space-y-3">
        {/* 제목 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            제목
          </label>
          {editing ? (
            <div>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={autoTitle || "문서 제목"}
                value={form.title}
                onChange={(e) => onForm("title", e.target.value)}
              />
              {autoTitle && (
                <p className="mt-1 text-xs text-gray-500">
                  미입력 시 자동 제목: {autoTitle}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-900">{form.title || autoTitle}</p>
          )}
        </div>

        {/* 작성일 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            작성일
          </label>
          <p className="text-sm text-gray-900">{today}</p>
        </div>

        {/* 작성자 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            작성자
          </label>
          <p className="text-sm text-gray-900">{me?.name}</p>
        </div>

        {/* 비고 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            비고
          </label>
          {editing ? (
            <TinyMCEeditor
              initialValue={form.note || ""}
              editorRef={editorRef}
              onChange={(v) => onForm("note", v)}
            />
          ) : (
            <div
              className="text-sm text-gray-900 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: form.note }}
            />
          )}
        </div>
      </section>

      {/* 사고 상세 정보 (모바일 최적화) */}
      <section className="bg-white rounded-lg p-3 border border-gray-200 space-y-3">
        <h3 className="text-sm font-medium text-gray-900">사고 상세 정보</h3>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            사고번호
          </label>
          {editing ? (
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.incident_no}
              onChange={(e) => onMeta("incident_no", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{meta.incident_no || "-"}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            사고일자 *
          </label>
          {editing ? (
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.incident_date}
              onChange={(e) => onMeta("incident_date", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{meta.incident_date}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            장소
          </label>
          {editing ? (
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.location}
              onChange={(e) => onMeta("location", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{meta.location || "-"}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            사고유형
          </label>
          {editing ? (
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.incident_type}
              onChange={(e) => onMeta("incident_type", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{meta.incident_type || "-"}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            중대도
          </label>
          {editing ? (
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.severity}
              onChange={(e) => onMeta("severity", e.target.value)}
            >
              <option value="LOW">LOW</option>
              <option value="MODERATE">MODERATE</option>
              <option value="HIGH">HIGH</option>
              <option value="CRITICAL">CRITICAL</option>
            </select>
          ) : (
            <p className="text-sm text-gray-900">{meta.severity}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            신고자 *
          </label>
          {editing ? (
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.reporter}
              onChange={(e) => onMeta("reporter", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{meta.reporter || "-"}</p>
          )}
        </div>

        {/* 목격자 */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            목격자
          </label>
          {editing ? (
            <div className="space-y-2">
              {(meta.witnesses || []).map((w, i) => {
                const id = w?.id ?? i;
                const val = typeof w === "string" ? w : w?.name || "";
                return (
                  <div key={id} className="flex gap-2">
                    <input
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={val}
                      onChange={(e) => updateWitness(id, e.target.value)}
                      placeholder="이름 (연락처/소속)"
                    />
                    <button
                      type="button"
                      className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-xs hover:bg-red-100"
                      onClick={() => removeWitness(id)}
                    >
                      삭제
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
                onClick={addWitness}
              >
                목격자 추가
              </button>
              <p className="text-xs text-gray-500">
                목격자 진술은 가능한 한 구체적으로 기록하세요.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-900">
              {(meta.witnesses || [])
                .map((w) => (typeof w === "string" ? w : w?.name || ""))
                .filter(Boolean)
                .join(", ") || "-"}
            </p>
          )}
        </div>
      </section>

      {/* 피해 / 비용 */}
      <section className="bg-white rounded-lg p-3 border border-gray-200 space-y-3">
        <h3 className="text-sm font-medium text-gray-900">피해 / 비용</h3>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              경상자
            </label>
            <input
              type="number"
              min={0}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
              value={meta.minor_injury_count}
              onChange={(e) =>
                onMeta("minor_injury_count", Number(e.target.value || 0))
              }
              disabled={!editing}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              중상자
            </label>
            <input
              type="number"
              min={0}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
              value={meta.major_injury_count}
              onChange={(e) =>
                onMeta("major_injury_count", Number(e.target.value || 0))
              }
              disabled={!editing}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              사망자
            </label>
            <input
              type="number"
              min={0}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
              value={meta.fatality_count}
              onChange={(e) =>
                onMeta("fatality_count", Number(e.target.value || 0))
              }
              disabled={!editing}
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          총 인명피해:{" "}
          {meta.minor_injury_count +
            meta.major_injury_count +
            meta.fatality_count}
          명
        </p>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            재산 피해액 (원)
          </label>
          {editing ? (
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={meta.property_damage_cost}
              onChange={(e) =>
                onMeta("property_damage_cost", Number(e.target.value || 0))
              }
              placeholder="숫자만 입력"
            />
          ) : (
            <p className="text-sm text-gray-900">
              {meta.property_damage_cost?.toLocaleString?.() ??
                meta.property_damage_cost}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            재산 피해 메모
          </label>
          {editing ? (
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={3}
              value={meta.property_damage_note}
              onChange={(e) => onMeta("property_damage_note", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">
              {meta.property_damage_note || "-"}
            </p>
          )}
        </div>
      </section>

      {/* 사고 설명 / 원인 / 조치 */}
      <section className="bg-white rounded-lg p-3 border border-gray-200 space-y-3">
        <h3 className="text-sm font-medium text-gray-900">
          사고 설명 / 원인 / 조치
        </h3>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            사고 개요 *
          </label>
          {editing ? (
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={5}
              value={meta.description}
              onChange={(e) => onMeta("description", e.target.value)}
              placeholder="무엇이, 언제, 어디서, 누구에게, 어떻게 발생했는지 상세히"
            />
          ) : (
            <p className="text-sm text-gray-900 whitespace-pre-wrap">
              {meta.description || "-"}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            원인 분석
          </label>
          {editing ? (
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={4}
              value={meta.cause}
              onChange={(e) => onMeta("cause", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900 whitespace-pre-wrap">
              {meta.cause || "-"}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            즉시 시정조치
          </label>
          {editing ? (
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={4}
              value={meta.corrective_action}
              onChange={(e) => onMeta("corrective_action", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900 whitespace-pre-wrap">
              {meta.corrective_action || "-"}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            재발 방지대책
          </label>
          {editing ? (
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={4}
              value={meta.preventive_action}
              onChange={(e) => onMeta("preventive_action", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900 whitespace-pre-wrap">
              {meta.preventive_action || "-"}
            </p>
          )}
        </div>
      </section>

      {/* 열람 권한 설정 */}
      <section className="bg-white rounded-lg p-3 border border-gray-200 space-y-3">
        <h3 className="text-sm font-medium text-gray-900">열람 권한</h3>

        {!readOnly && (
          <>
            <button
              className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
              onClick={openReferenceModal}
            >
              열람 사용자 선택
            </button>

            {referenceUserDetails.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {referenceUserDetails.map((u) => (
                  <span
                    key={u.id}
                    className="px-2 py-1 rounded bg-gray-100 border text-xs"
                  >
                    {u.last_name}
                    {u.first_name}
                    {u.position && (
                      <span className="text-gray-500"> {u.position}</span>
                    )}
                    <span className="text-gray-500"> ({u.company})</span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {readOnly && referenceUserDetails?.length > 0 && (
          <div className="flex flex-wrap gap-1 text-sm text-gray-700">
            {referenceUserDetails.map((u) => (
              <span
                key={u.id}
                className="px-2 py-1 rounded bg-gray-100 border text-xs"
              >
                {u.last_name}
                {u.first_name} ({u.position}, {u.company})
              </span>
            ))}
          </div>
        )}
      </section>

      {/* modal */}
      <ReferenceModal
        isOpen={isReferenceModalOpen}
        onClose={closeReferenceModal}
        selectedIds={referenceUserIds}
        onChange={handleReferenceChange}
        onSave={handleReferenceSave}
        name="열람 가능 사용자"
      />
    </div>
  );
}
